import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface CanvasPosition {
  x: number;
  y: number;
}

export interface CanvasItem {
  id: string;
  type: 'drawing' | 'sticker' | 'photo' | 'video' | 'text' | 'bulletin';
  position: { x: number; y: number };
  data: any;
  userId: string;
  createdAt: Date;
  updatedAt?: Date;
  rotation?: number;
  scale?: number;
  zIndex?: number;
}

export interface DrawingStroke {
  id: string;
  points: { x: number; y: number }[];
  color: string;
  width: number;
  tool: 'pen' | 'marker' | 'highlighter';
  section: { x: number; y: number };
}

export interface StickerInstance {
  id: string;
  stickerId: string;
  position: { x: number; y: number };
  rotation: number;
  scale: number;
  isPeeling: boolean;
  zIndex: number;
  section: { x: number; y: number };
}

export const useCanvasItems = (canvasId = 'main') => {
  const [items, setItems] = useState<Map<string, CanvasItem>>(new Map());
  const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set());
  const channelRef = useRef<any>(null);
  // Keep refs to avoid dependency churn and dedupe concurrent loads
  const loadedSectionsRef = useRef<Set<string>>(new Set());
  const inFlightSectionsRef = useRef<Set<string>>(new Set());
  const lastRequestedKeyRef = useRef<string>('');
  // Map temporary (non-UUID) ids to persisted UUID ids
  const tempIdMapRef = useRef<Map<string, string>>(new Map());
  // Save status per item for debugging
  const saveStatusRef = useRef<Map<string, { pending: boolean; lastSavedAt: Date | null; lastSavedPosition?: { x: number; y: number } }>>(new Map());
  // Supabase connection/loading debug meta
  const realtimeConnectedRef = useRef<boolean>(false);
  const lastLoadAtRef = useRef<Date | null>(null);
  const lastLoadErrorRef = useRef<string | null>(null);

  // Simple UUID v4 checker
  const isUuid = useCallback((value: string) => {
    // Accept any UUID version
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }, []);

  // Deterministically map any string to a UUID-shaped value (stable across sessions)
  const deterministicUuidFromString = useCallback((input: string) => {
    // Produce 128 bits by combining four 32-bit hashes with different seeds
    const hash32 = (str: string, seed: number) => {
      let h = seed >>> 0;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x45d9f3b) >>> 0;
        h ^= h >>> 13;
      }
      h = Math.imul(h ^ (h >>> 16), 2246822507) >>> 0;
      h = Math.imul(h ^ (h >>> 13), 3266489909) >>> 0;
      h ^= h >>> 16;
      return h >>> 0;
    };
    const s1 = hash32(input, 0x12345678).toString(16).padStart(8, '0');
    const s2 = hash32(input + '#', 0x9e3779b9).toString(16).padStart(8, '0');
    const s3 = hash32('#' + input, 0x7f4a7c15).toString(16).padStart(8, '0');
    const s4 = hash32(input + '::', 0xf39cc060).toString(16).padStart(8, '0');
    // Format as UUID v4-like (set version nibble to '4' and variant to '8')
    const part1 = s1;
    const part2 = s2.substring(0, 4);
    const part3 = '4' + s2.substring(5, 8); // ensure version 4 pattern
    const part4 = '8' + s3.substring(1, 4); // variant 10xx
    const part5 = (s3.substring(4) + s4).substring(0, 12);
    return `${part1}-${part2}-${part3}-${part4}-${part5}`.toLowerCase();
  }, []);

  // Compute effective canvas UUID (use provided if valid, else deterministic mapping)
  const effectiveCanvasId = useMemo(() => {
    const raw = String(canvasId);
    return isUuid(raw) ? raw : deterministicUuidFromString(`canvas:${raw}`);
  }, [canvasId, isUuid, deterministicUuidFromString]);

  // Sync ref with state
  useEffect(() => {
    loadedSectionsRef.current = loadedSections;
  }, [loadedSections]);

  // Calculate section key from position
  const getSectionKey = useCallback((position: CanvasPosition) => {
    const sectionSize = 1000;
    const sectionX = Math.floor(position.x / sectionSize);
    const sectionY = Math.floor(position.y / sectionSize);
    return `${sectionX},${sectionY}`;
  }, []);

  // Get sections that should be loaded for current viewport
  const getRequiredSections = useCallback((viewport: CanvasPosition, zoom: number) => {
    const sectionSize = 1000;
    const viewportSize = 2000 / zoom; // Load extra area around viewport
    
    const minX = Math.floor((viewport.x - viewportSize) / sectionSize);
    const maxX = Math.floor((viewport.x + viewportSize) / sectionSize);
    const minY = Math.floor((viewport.y - viewportSize) / sectionSize);
    const maxY = Math.floor((viewport.y + viewportSize) / sectionSize);
    
    const sections: string[] = [];
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        sections.push(`${x},${y}`);
      }
    }
    return sections;
  }, []);

  // Load items from a specific section
  const loadSection = useCallback(async (sectionKey: string) => {
    // Fast exits for already loaded or in-flight
    if (loadedSectionsRef.current.has(sectionKey) || inFlightSectionsRef.current.has(sectionKey)) return;
    inFlightSectionsRef.current.add(sectionKey);

    try {
      console.log(`📍 Loading canvas section ${sectionKey} for canvas ID: "${canvasId}" (effective: "${effectiveCanvasId}")`); 
      const [sectionX, sectionY] = sectionKey.split(',').map(Number);
      const sectionSize = 1000;
      
      const minX = sectionX * sectionSize;
      const maxX = (sectionX + 1) * sectionSize;
      const minY = sectionY * sectionSize;
      const maxY = (sectionY + 1) * sectionSize;
      
      // Query canvas items from database for this section using section_id for robust filtering
      let primaryQuery = supabase
        .from('canvas_items')
        .select('*')
        .eq('section_id', sectionKey)
        .eq('canvas_id', effectiveCanvasId);
      const primary = await primaryQuery.order('created_at', { ascending: true });

      let data = primary.data;
      let error = primary.error;
      // Fallback: if section_id doesn't exist or query fails, use JSON position bounds
      if (error) {
        console.warn(`⚠️ section_id query failed; falling back to position bounds for section ${sectionKey}`, error);
        let fallbackQuery = supabase
          .from('canvas_items')
          .select('*')
          .gte('position->x', minX)
          .lt('position->x', maxX)
          .gte('position->y', minY)
          .lt('position->y', maxY)
          .eq('canvas_id', effectiveCanvasId);
        const fallback = await fallbackQuery.order('created_at', { ascending: true });
        data = fallback.data;
        error = fallback.error;

        // Second fallback: if JSON position filtering is not supported, filter using numeric columns
        if (error) {
          console.warn(`⚠️ JSON position filter failed; falling back to numeric bounds for section ${sectionKey}`, error);
          let numericQuery = supabase
            .from('canvas_items')
            .select('*')
            .gte('position_x', minX)
            .lt('position_x', maxX)
            .gte('position_y', minY)
            .lt('position_y', maxY)
            .eq('canvas_id', effectiveCanvasId);
          const numeric = await numericQuery.order('created_at', { ascending: true });
          data = numeric.data;
          error = numeric.error;
        }
      }

      if (error) {
        console.error(`❌ Error loading canvas section ${sectionKey} for canvas ID "${canvasId}":`, error);
        console.error(`Query parameters: minX=${minX}, maxX=${maxX}, minY=${minY}, maxY=${maxY}`);
        return;
      }
      
      console.log(`✅ Successfully loaded canvas section ${sectionKey} for canvas ID "${canvasId}"`, { itemCount: data?.length || 0 });

      // Convert database items to canvas items format (resilient to schema diffs)
      const sectionItems = data?.map((dbItem): CanvasItem => {
        const anyItem: any = dbItem as any;
        const posFromJson = anyItem.position as { x?: number; y?: number } | undefined;
        const x = posFromJson?.x ?? anyItem.position_x ?? 0;
        const y = posFromJson?.y ?? anyItem.position_y ?? 0;
        const t = (anyItem.type ?? anyItem.item_type) as CanvasItem['type'];
        return {
          id: anyItem.id,
          type: t,
          position: { x, y },
          // Use content_data column which exists in the schema
          data: anyItem.content_data ?? anyItem.content ?? {},
          userId: anyItem.created_by ?? anyItem.user_id,
          createdAt: new Date(anyItem.created_at),
          updatedAt: anyItem.updated_at ? new Date(anyItem.updated_at) : undefined,
          rotation: anyItem.rotation || 0,
          scale: anyItem.scale || 1,
          zIndex: anyItem.z_index || 1
        };
      }) || [];

      // Add items to local state
      setItems(prev => {
        const newItems = new Map(prev);
        sectionItems.forEach(item => newItems.set(item.id, item));
        return newItems;
      });
      
      console.log(`Loaded ${sectionItems.length} items from canvas section: ${sectionKey}`);
      setLoadedSections(prev => {
        const next = new Set(prev);
        next.add(sectionKey);
        return next;
      });
      // Keep ref in sync immediately for this tick
      loadedSectionsRef.current.add(sectionKey);
    } catch (error) {
      console.error('Error loading canvas section:', error);
    } finally {
      inFlightSectionsRef.current.delete(sectionKey);
    }
  }, [effectiveCanvasId]);


  // Add new item
  const addItem = useCallback(async (item: CanvasItem) => {
    // Ensure DB-compatible ID (UUID). If not, generate one and update local state before persisting.
    const needsUuid = !isUuid(item.id);
    const persistedId = needsUuid && (globalThis as any).crypto?.randomUUID ? (globalThis as any).crypto.randomUUID() : item.id;
    const persistedItem: CanvasItem = needsUuid ? { ...item, id: persistedId } : item;

    // Add to local state immediately for responsive UI (with the persisted ID)
    setItems(prev => new Map(prev).set(persistedItem.id, persistedItem));
    // Mark as pending save
    saveStatusRef.current.set(persistedItem.id, { pending: true, lastSavedAt: null });

    // Record mapping so callers using the old id can still update/delete
    if (needsUuid) {
      tempIdMapRef.current.set(item.id, persistedId);
    }

    try {
      console.log(`➕ Adding canvas item to database:`, { 
        id: persistedItem.id,
        type: persistedItem.type, 
        canvasId,
        effectiveCanvasId
      });
      // Calculate section for database organization
      const sectionKey = getSectionKey(persistedItem.position);
      
      // Save to database using the actual schema columns
      // user_id must be a valid UUID or null
      const validUserId = persistedItem.userId && persistedItem.userId !== 'anonymous' ? persistedItem.userId : null;
      
      // Map type values to match database constraints
      const dbItemType = persistedItem.type === 'photo' ? 'image' : persistedItem.type;
      
      const primaryPayload: any = {
        id: persistedItem.id,
        type: persistedItem.type,
        item_type: dbItemType, // Map 'photo' to 'image' for item_type constraint
        position: persistedItem.position,
        position_x: persistedItem.position.x,
        position_y: persistedItem.position.y,
        content_data: persistedItem.data, // Use content_data column that exists
        user_id: validUserId,
        rotation: persistedItem.rotation || 0,
        z_index: persistedItem.zIndex || 1,
        canvas_id: effectiveCanvasId,
        section_id: sectionKey // ✅ FIX: Include section_id for spatial indexing!
      };
      
      console.log('🔄 Attempting to insert canvas item with payload:', primaryPayload);
      const { error: insertError } = await supabase
        .from('canvas_items')
        .insert(primaryPayload as any);
      if (insertError) {
        console.error('❌ Error saving canvas item to database:', insertError);
        console.error('Failed item data:', { 
          id: persistedItem.id, 
          canvasId, 
          type: persistedItem.type,
          position: persistedItem.position,
          payload: primaryPayload
        });
        // Keep pending true so debug shows unsaved state
        return;
      }

      console.log('✅ Canvas item successfully inserted to database:', persistedItem.id);
      // Update save status
      saveStatusRef.current.set(persistedItem.id, { 
        pending: false, 
        lastSavedAt: new Date(), 
        lastSavedPosition: { ...persistedItem.position }
      });
      
      // Broadcast to other users via realtime
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'item_added',
          payload: { item: persistedItem }
        });
      }

      if (needsUuid) {
        console.log(`🔁 Re-mapped temporary local id to DB UUID: ${item.id} -> ${persistedItem.id}`);
      }
      console.log('Canvas item saved successfully:', persistedItem.id);
    } catch (error) {
      console.error('Error saving canvas item:', error);
      // Keep the local item; do not remove on insert error to avoid disappearing UI artifacts.
    }
  }, [getSectionKey, isUuid, effectiveCanvasId]);

  // Update existing item
  const updateItem = useCallback(async (itemId: string, updates: Partial<CanvasItem>) => {
    // Resolve to persisted UUID if a temp id was used
    const resolvedId = isUuid(itemId) ? itemId : (tempIdMapRef.current.get(itemId) ?? itemId);
    const originalItem = items.get(resolvedId) || items.get(itemId);
    
    // Update local state immediately
    setItems(prev => {
      const newItems = new Map(prev);
      // Try resolved id first
      let existingItem = newItems.get(resolvedId);
      if (!existingItem) existingItem = newItems.get(itemId);
      if (existingItem) {
        const nextItem = { ...existingItem, ...updates, updatedAt: new Date() } as CanvasItem;
        // If we had both keys (rare), clean up temp one and set only the resolved id
        if (newItems.has(itemId) && itemId !== resolvedId) newItems.delete(itemId);
        newItems.set(resolvedId, nextItem);
      }
      return newItems;
    });

    try {
      // Mark as pending
      const targetId = resolvedId;
      const status = saveStatusRef.current.get(targetId) || { pending: false, lastSavedAt: null };
      saveStatusRef.current.set(targetId, { ...status, pending: true });

      // Prepare database update using actual schema columns
      const dbUpdate: any = {};
      if (updates.position) {
        dbUpdate.position = updates.position;
        dbUpdate.position_x = updates.position.x;
        dbUpdate.position_y = updates.position.y;
        dbUpdate.section_id = getSectionKey(updates.position);
      }
      if (updates.rotation !== undefined) {
        dbUpdate.rotation = updates.rotation;
      }
      if (updates.zIndex !== undefined) {
        dbUpdate.z_index = updates.zIndex;
      }
      if (updates.data !== undefined) {
        dbUpdate.content_data = updates.data;
      }

      // Update the database
      const { error } = await supabase
        .from('canvas_items')
        .update(dbUpdate)
        .eq('id', resolvedId);

      if (error) {
        console.error('❌ Error updating canvas item in database:', error);
        // Revert local state on error
        if (originalItem) {
          setItems(prev => new Map(prev).set(resolvedId, originalItem));
        }
        // Reset pending flag but do not update lastSavedAt/position
        const current = saveStatusRef.current.get(resolvedId) || { pending: false, lastSavedAt: null };
        saveStatusRef.current.set(resolvedId, { ...current, pending: false });
        return;
      }
      
      // Broadcast to other users
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'item_updated',
          payload: { itemId, updates }
        });
      }

      console.log('Canvas item updated successfully:', resolvedId);
      // Update save status
      const current = saveStatusRef.current.get(resolvedId) || { pending: false, lastSavedAt: null };
      saveStatusRef.current.set(resolvedId, { 
        pending: false, 
        lastSavedAt: new Date(),
        lastSavedPosition: updates.position ? { ...updates.position } : current.lastSavedPosition
      });
    } catch (error) {
      console.error('Error updating canvas item:', error);
      // Revert local state on error
      if (originalItem) {
        setItems(prev => new Map(prev).set(resolvedId, originalItem));
      }
      // Reset pending flag
      const current = saveStatusRef.current.get(resolvedId) || { pending: false, lastSavedAt: null };
      saveStatusRef.current.set(resolvedId, { ...current, pending: false });
    }
  }, [getSectionKey, items, isUuid]);

  // Remove item
  const removeItem = useCallback(async (itemId: string) => {
    const resolvedId = isUuid(itemId) ? itemId : (tempIdMapRef.current.get(itemId) ?? itemId);
    // Optimistically remove from local state
    setItems(prev => {
      const next = new Map(prev);
      next.delete(resolvedId);
      return next;
    });

    try {
      const { error } = await supabase
        .from('canvas_items')
        .delete()
        .eq('id', resolvedId);
      if (error) {
        console.error('❌ Error removing canvas item from database:', error);
      } else if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'item_removed',
          payload: { itemId: resolvedId }
        });
      }
    } catch (err) {
      console.error('Error removing canvas item:', err);
    }
  }, [isUuid]);

  // Load sections based on viewport
  const loadViewportSections = useCallback(async (viewport: { x: number; y: number }, zoom: number) => {
    const key = JSON.stringify({ v: viewport, z: zoom });
    if (lastRequestedKeyRef.current === key) return; // throttle identical consecutive requests
    lastRequestedKeyRef.current = key;
    const targetSections = getRequiredSections(viewport, zoom);
    const sectionsToLoad = targetSections.filter(s => !loadedSectionsRef.current.has(s) && !inFlightSectionsRef.current.has(s));
    try {
      await Promise.all(sectionsToLoad.map(s => loadSection(s)));
      lastLoadAtRef.current = new Date();
      lastLoadErrorRef.current = null;
    } catch (error: any) {
      console.error('Error loading sections:', error);
      lastLoadErrorRef.current = error?.message || String(error);
    }
  }, [getRequiredSections, loadSection]);

  // Initialize realtime subscription
  useEffect(() => {
    const channel = supabase.channel('canvas-items-broadcast');
    channelRef.current = channel;
    realtimeConnectedRef.current = true;

    channel
      .on('broadcast', { event: 'item_added' }, (payload: any) => {
        const item = payload.payload.item as CanvasItem;
        setItems(prev => new Map(prev).set(item.id, item));
      })
      .on('broadcast', { event: 'item_updated' }, (payload) => {
        const { itemId, updates } = payload.payload;
        setItems(prev => {
          const newItems = new Map(prev);
          const existingItem = newItems.get(itemId);
          if (existingItem) {
            newItems.set(itemId, { ...existingItem, ...updates, updatedAt: new Date() });
          }
          return newItems;
        });
      })
      .on('broadcast', { event: 'item_removed' }, (payload) => {
        const { itemId } = payload.payload;
        setItems(prev => {
          const newItems = new Map(prev);
          newItems.delete(itemId);
          return newItems;
        });
      })
      .subscribe();

    return () => {
      realtimeConnectedRef.current = false;
      channel.unsubscribe();
    };
  }, []);

  const itemsArray = useMemo(() => Array.from(items.values()), [items]);

  // Expose a debug snapshot for UI panels
  const getDebugSnapshot = useCallback(() => {
    return itemsArray.map(it => {
      const status = saveStatusRef.current.get(it.id) || { pending: false, lastSavedAt: null };
      return {
        id: it.id,
        type: it.type,
        position: it.position,
        lastSavedAt: status.lastSavedAt,
        lastSavedPosition: status.lastSavedPosition,
        pending: status.pending
      };
    });
  }, [itemsArray]);

  const getDebugMeta = useCallback(() => {
    return {
      realtimeConnected: realtimeConnectedRef.current,
      lastLoadAt: lastLoadAtRef.current,
      lastLoadError: lastLoadErrorRef.current,
      inFlightCount: inFlightSectionsRef.current.size,
      loadedCount: loadedSectionsRef.current.size,
      lastRequestedKey: lastRequestedKeyRef.current,
      totalItems: itemsArray.length,
    };
  }, [itemsArray.length]);

  return {
    items: itemsArray,
    addItem,
    updateItem,
    removeItem,
    loadViewportSections,
    getDebugSnapshot,
    getDebugMeta
  };
};
